import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import { AlertCircle, Loader2, Edit2, Trash2 } from "lucide-react";
import Card from "../common/Card";
import SectionHeader from "../common/SectionHeader";
import FileUploadZone from "../uploads/FileUploadZone";
import FileTable from "../uploads/FileTable";
import PrimaryButton from "../ui/PrimaryButton";
import Toast from "../common/Toast";
import * as XLSX from "xlsx";
import apiClient from "../utils/apiClient";
import ProfileService from "../services/ProfileService";

const LoadingScreen = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
  </div>
);

const ErrorScreen = ({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-screen">
    <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
    <p className="text-red-600">Error: {message}</p>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-12 bg-gray-50 rounded-lg">
    <p className="text-gray-600">{message || "No data available."}</p>
  </div>
);

const RetrofitAndEnergyComponent = () => {
  const { projectId } = useParams();

  const [retrofitFileData, setRetrofitFileData] = useState(null);
  const [retrofitPreviewData, setRetrofitPreviewData] = useState(null);
  const [retrofitMeasures, setRetrofitMeasures] = useState([]);
  const [selectedRetrofits, setSelectedRetrofits] = useState([]);

  const [energyMixFileData, setEnergyMixFileData] = useState(null);
  const [energyMixPreviewData, setEnergyMixPreviewData] = useState(null);
  const [energyMixes, setEnergyMixes] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const apiRoot = process.env.REACT_APP_API_ROOT;
  const token = localStorage.getItem("token");
  const profileService = new ProfileService(apiRoot, token);

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const retrofitResponse = await apiClient.get("/api/retrofit/", {
        params: { project_id: projectId },
      });
      setRetrofitMeasures(retrofitResponse || []);

      const energyMixResponse = await apiClient.get("/api/energy_mix/", {
        params: { project_id: projectId },
      });
      setEnergyMixes(energyMixResponse || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch existing data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetrofitFilesSelect = async (files) => {
    if (files.length === 0) return;

    setRetrofitFileData(files[0]);

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        setRetrofitPreviewData(jsonData.slice(0, 5));
      };
      reader.readAsArrayBuffer(files[0]);
    } catch (error) {
      console.error("Error reading Excel file:", error);
      showToast(
        "Error reading retrofit Excel file. Please try again.",
        "error"
      );
    }
  };

  const handleEnergyMixFilesSelect = async (files) => {
    if (files.length === 0) return;

    setEnergyMixFileData(files[0]);

    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        setEnergyMixPreviewData(jsonData.slice(0, 5));
      };
      reader.readAsArrayBuffer(files[0]);
    } catch (error) {
      console.error("Error reading Excel file:", error);
      showToast(
        "Error reading energy mix Excel file. Please try again.",
        "error"
      );
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  const processRetrofitFile = async () => {
    try {
      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            let created = 0;
            let errors = 0;

            for (const row of jsonData) {
              try {
                const retrofitData = {
                  project: projectId,
                  name: row["Name"],
                  component: row["Component"] || "Building Envelop",
                  sub_component:
                    row["Sub Component"] || row["Sub-Component"] || "Windows",
                  type_of_measure: row["Type of measure"] || "Replacement",
                  measure_description: row["Measure Description"] || "",
                  adaptation: row["Adaptation [%]"] || null,
                  reduction_in_electricity_end_energy:
                    row["Reduction in Electricity End Energy [kWh/m²]"] || 0,
                  reduction_in_heat_end_energy:
                    row["Reduction in Heat End Energy [kWh/m²]"] ||
                    row["Reduction in Heat End Energy [kWh/m²]"] ||
                    0,
                  other_cost_saving: row["Other Cost savings [€/a]"] || 0,
                  maintanance_cost:
                    row["Maintenance Costs [€/a]"] ||
                    row["Maintenace Costs [€/a/m²]"] ||
                    0,
                  services_cost:
                    row["Service Costs [€/a]"] ||
                    row["Service Costs [€/a/m²]"] ||
                    0,
                  investments: row["Investment Costs [€/m²]"] || null,
                  own_capital: row["Own Captial [€/m²]"] || null,
                  subsidies: row["Subsidies [€/m²]"] || null,
                  foreign_capital:
                    row["Foreign Capital [€/m²]"] ||
                    row["Foreign Capital [€]"] ||
                    null,
                  interest_rate:
                    row["Interest Rate [%]"] ||
                    row["Interest Rate [%]"] ||
                    null,
                  Loan_type: row["Loan Type"] || "Fixed Loan",
                  repayment_time: row["Repayment time [years]"] || null,
                  reduction_in_energy_demand:
                    row["Reduction in Heat Energy Demand [kWh/m²]"] ||
                    row["Reduction in Heat Energy Demand [kWh]"] ||
                    row["Reduction in Energy Demand [kWh]"] ||
                    null,
                  electricity_reduction:
                    row["Redution in Electricity Demand [kWh/m²]"] || null,
                  inflation_rate: row["Inflation Rate [%]"] || null,
                  embodied_carbon: row["Embodied Carbon [kg/CO2/m²]"] || 0,
                  reduction_in_electricity_primary_energy: 0,
                };

                await apiClient.post("/api/retrofit/", retrofitData);
                created++;
              } catch (error) {
                console.error("Error uploading retrofit row:", error);
                errors++;
              }
            }

            resolve({ created, errors });
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(retrofitFileData);
      });
    } catch (error) {
      throw error;
    }
  };

  const processEnergyMixFile = async () => {
    try {
      const existingEntries = await apiClient.get("/api/energy_mix/", {
        params: { project_id: projectId },
      });

      for (const entry of existingEntries) {
        await apiClient.delete(`/api/energy_mix/${entry.id}/`);
      }

      const reader = new FileReader();
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            console.log("Excel data with headers:", jsonData);

            const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
            console.log("Excel raw data:", rawData);

            if (rawData.length > 0) {
              console.log("Excel column headers:", rawData[0]);
            }

            if (jsonData.length === 0) {
              throw new Error("No data found in the Excel file");
            }
            const sampleRow = jsonData[0];
            console.log("Sample row keys:", Object.keys(sampleRow));
            let created = 0;
            let errors = 0;

            for (const row of jsonData) {
              try {
                const rowKeys = Object.keys(row);
                console.log("Processing row with keys:", rowKeys);
                let utilityMixKey = rowKeys.find(
                  (key) =>
                    key.toLowerCase().includes("utility") ||
                    key.toLowerCase().includes("mix")
                );

                let costPerKwhKey = rowKeys.find(
                  (key) =>
                    (key.toLowerCase().includes("cost") &&
                      key.toLowerCase().includes("kwh")) ||
                    key.toLowerCase().includes("per")
                );

                let costIncreaseKey = rowKeys.find(
                  (key) =>
                    key.toLowerCase().includes("increase") ||
                    (key.toLowerCase().includes("cost") &&
                      key.toLowerCase().includes("year"))
                );

                let co2EmissionsKey = rowKeys.find(
                  (key) =>
                    key.toLowerCase().includes("co2") ||
                    key.toLowerCase().includes("emission")
                );

                console.log("Mapped column keys:", {
                  utilityMixKey,
                  costPerKwhKey,
                  costIncreaseKey,
                  co2EmissionsKey,
                });

                const utilityMix = utilityMixKey
                  ? row[utilityMixKey]
                  : "Unknown";
                const costPerKwh = costPerKwhKey ? row[costPerKwhKey] : null;
                const costIncreasePerYear = costIncreaseKey
                  ? row[costIncreaseKey]
                  : null;
                const co2EmissionsPerKwh = co2EmissionsKey
                  ? row[co2EmissionsKey]
                  : null;

                console.log("Extracted values:", {
                  utilityMix,
                  costPerKwh,
                  costIncreasePerYear,
                  co2EmissionsPerKwh,
                });

                const energyMixData = {
                  project: parseInt(projectId),
                  utility_mix: utilityMix || "Unknown",
                  cost_per_kwh: parseFloat(costPerKwh) || null,
                  cost_increase_per_year:
                    parseFloat(costIncreasePerYear) || null,
                  co2_emissions_per_kwh: parseFloat(co2EmissionsPerKwh) || null,
                };

                console.log("Sending to API:", energyMixData);

                // Use apiClient instead of direct fetch
                const response = await apiClient.post(
                  "/api/energy_mix/",
                  energyMixData
                );
                console.log("API response:", response);
                created++;
              } catch (error) {
                console.error("Error uploading energy mix row:", error);
                errors++;
              }
            }

            resolve({ created, errors });
          } catch (error) {
            console.error("Error processing energy mix file:", error);
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(energyMixFileData);
      });
    } catch (error) {
      console.error("Energy mix processing error:", error);
      throw error;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!retrofitFileData && !energyMixFileData) {
      showToast("Please select at least one file to upload", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const results = {
        retrofit: { created: 0, errors: 0, processed: false },
        energyMix: { created: 0, errors: 0, processed: false },
      };
      if (retrofitFileData) {
        const retrofitResults = await processRetrofitFile();
        results.retrofit = { ...retrofitResults, processed: true };
        if (retrofitResults.created > 0) {
          try {
            await profileService.updateModernization(projectId, true);
            console.log("Modernization status updated successfully");
          } catch (error) {
            console.error("Error updating modernization status:", error);
          }
        }
      }

      if (energyMixFileData) {
        const energyMixResults = await processEnergyMixFile();
        results.energyMix = { ...energyMixResults, processed: true };
      }

      let message = "Upload completed. ";
      if (results.retrofit.processed) {
        message += `Retrofit measures: ${results.retrofit.created} created, ${results.retrofit.errors} errors. `;
      }
      if (results.energyMix.processed) {
        message += `Energy mix: ${results.energyMix.created} created, ${results.energyMix.errors} errors.`;
      }

      const hasErrors =
        results.retrofit.errors > 0 || results.energyMix.errors > 0;
      showToast(message, hasErrors ? "warning" : "success");

      setRetrofitFileData(null);
      setRetrofitPreviewData(null);
      setEnergyMixFileData(null);
      setEnergyMixPreviewData(null);
      fetchData();
    } catch (error) {
      console.error("Error processing files:", error);
      showToast(
        error.message || "Error processing files. Please try again.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectRetrofit = (id) => {
    setSelectedRetrofits((prev) => {
      if (prev.includes(id)) {
        return prev.filter((itemId) => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAllRetrofits = (isSelected) => {
    if (isSelected) {
      const allIds = retrofitMeasures.map((item) => item.id);
      setSelectedRetrofits(allIds);
    } else {
      setSelectedRetrofits([]);
    }
  };

  const handleDeleteRetrofits = async () => {
    if (selectedRetrofits.length === 0) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${selectedRetrofits.length} retrofit measure(s)?`
    );

    if (!confirmDelete) return;

    setIsDeleting(true);

    try {
      let deleted = 0;
      let failed = 0;

      for (const id of selectedRetrofits) {
        try {
          await apiClient.delete(`/api/retrofit/${id}/`);
          deleted++;
        } catch (error) {
          console.error(`Error deleting retrofit ID ${id}:`, error);
          failed++;
        }
      }

      showToast(
        `Deleted ${deleted} retrofit measure(s)${
          failed > 0 ? `, ${failed} failed` : ""
        }.`,
        failed > 0 ? "warning" : "success"
      );

      const remainingRetrofits = await apiClient.get("/api/retrofit/", {
        params: { project_id: projectId },
      });

      if (remainingRetrofits.length === 0) {
        try {
          await profileService.updateModernization(projectId, false);
          console.log("Modernization status updated to false");
        } catch (error) {
          console.error("Error updating modernization status:", error);
        }
      }

      fetchData();
      setSelectedRetrofits([]);
    } catch (error) {
      showToast(`Error deleting retrofit measures: ${error.message}`, "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const renderPreviewTable = (previewData) => {
    if (!previewData || previewData.length === 0) return null;

    return (
      <div className="overflow-x-auto mt-2">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden shadow-sm ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  {previewData[0]?.map((header, idx) => (
                    <th
                      key={idx}
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {previewData.slice(1).map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((cell, cellIdx) => (
                      <td
                        key={cellIdx}
                        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          Showing first {previewData.length - 1} rows of data. Full data will be
          processed on upload.
        </p>
      </div>
    );
  };

  if (isLoading) {
    return (
      <MainLayout>
        <LoadingScreen />
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <ErrorScreen message={error} />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card
            title={`Building Data Management - Project ${projectId}`}
            subtitle="Manage retrofit measures and energy mix data for your building"
          >
            <div className="space-y-6 p-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader
                    title="Manage Retrofit Measures"
                    action={
                      selectedRetrofits.length > 0 && (
                        <div className="flex space-x-2">
                          <PrimaryButton
                            onClick={handleDeleteRetrofits}
                            isLoading={isDeleting}
                            loadingText="Deleting..."
                            className="bg-red-600 hover:bg-red-700"
                            width="auto"
                          >
                            <div className="flex items-center">
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete ({selectedRetrofits.length})
                            </div>
                          </PrimaryButton>
                        </div>
                      )
                    }
                  />

                  {retrofitMeasures.length > 0 ? (
                    <FileTable
                      files={retrofitMeasures.map((measure) => ({
                        id: measure.id,
                        name: measure.name,
                        type: measure.component + " - " + measure.sub_component,
                        _created_at: null,
                        _updated_at: null,
                      }))}
                      selectedFiles={selectedRetrofits}
                      onSelectFile={handleSelectRetrofit}
                      onSelectAll={handleSelectAllRetrofits}
                    />
                  ) : (
                    <EmptyState message="No retrofit measures available. Upload new measures to get started." />
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Energy Mix Information" />

                  {energyMixes.length > 0 ? (
                    <div className="overflow-x-auto mt-2">
                      <div className="inline-block min-w-full align-middle">
                        <div className="overflow-hidden shadow-sm ring-1 ring-black ring-opacity-5 rounded-lg">
                          <table className="min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                  Utility Mix
                                </th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                  Cost per kWh [€]
                                </th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                  Cost Increase [%/year]
                                </th>
                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                  CO2 Emissions [kgCO2/kWh]
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {energyMixes.map((mix) => (
                                <tr key={mix.id}>
                                  <td className="px-3 py-4 text-sm text-gray-900 font-medium">
                                    {mix.utility_mix}
                                  </td>
                                  <td className="px-3 py-4 text-sm text-gray-500">
                                    {mix.cost_per_kwh}
                                  </td>
                                  <td className="px-3 py-4 text-sm text-gray-500">
                                    {mix.cost_increase_per_year}
                                  </td>
                                  <td className="px-3 py-4 text-sm text-gray-500">
                                    {mix.co2_emissions_per_kwh}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <EmptyState message="No energy mix data available. Upload energy mix data to get started." />
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6">
                  <SectionHeader title="Upload New Data" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div className="space-y-4">
                      <h3 className="text-base font-medium text-gray-900">
                        Retrofit Measures (Optional)
                      </h3>

                      <FileUploadZone
                        onFilesSelect={handleRetrofitFilesSelect}
                        disabled={isSubmitting}
                        accept=".xlsx,.xls"
                        acceptedTypes={["XLSX", "XLS"]}
                      />

                      {/* Preview Section */}
                      {retrofitPreviewData && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">
                            Retrofit Data Preview
                          </h4>
                          {renderPreviewTable(retrofitPreviewData)}
                        </div>
                      )}

                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Expected columns:
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Type of measure, Component, Sub-Component, Measure
                          Description, Adaptation [%], etc.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <h3 className="text-base font-medium text-gray-900">
                        Energy Mix Data (Optional)
                      </h3>

                      <FileUploadZone
                        onFilesSelect={handleEnergyMixFilesSelect}
                        disabled={isSubmitting}
                        accept=".xlsx,.xls"
                        acceptedTypes={["XLSX", "XLS"]}
                      />

                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-sm text-yellow-700">
                          <strong>Note:</strong> Uploading a new energy mix file
                          will replace all existing energy mix data.
                        </p>
                      </div>

                      {energyMixPreviewData && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-2">
                            Energy Mix Data Preview
                          </h4>
                          {renderPreviewTable(energyMixPreviewData)}
                        </div>
                      )}

                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          Expected columns:
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Utility Mix, Cost per kWh [€], Cost Increase per Year
                          [%], CO2 Emissions per kWh [kgCO2]
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <PrimaryButton
                      type="submit"
                      onClick={handleSubmit}
                      isLoading={isSubmitting}
                      disabled={!retrofitFileData && !energyMixFileData}
                      loadingText="Uploading..."
                    >
                      Upload Data
                    </PrimaryButton>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default RetrofitAndEnergyComponent;
